import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ExternalLink, Presentation, ShieldCheck } from 'lucide-react'
import { recordMeaningfulSalesInteraction } from './lib/api'
import { config } from './lib/config'
import {
  RECOMMENDED_SALES_DEMOS,
  SALES_DEMO_ROUTES,
  buildSalesDemoUrl,
  isCanonicalCrmId,
  type DemoAudience,
  type SalesDemoRoute,
} from './lib/salesDemo'
import './sales-demo-launcher.css'

type Props = {
  audience: DemoAudience | null
  organizationId?: string | null
  contactId?: string | null
  opportunityId?: string | null
  compact?: boolean
  label?: string
}

export function DemoLaunchAction({ audience, organizationId, contactId, opportunityId, compact = false, label = 'Launch Demo' }: Props) {
  const [open, setOpen] = useState(false)
  const recordKey = `${organizationId || ''}:${contactId || ''}:${opportunityId || ''}`
  const [feedback, setFeedback] = useState<Record<string, 'pending' | 'recorded' | 'unavailable'>>({})
  const pending = useRef(new Set<string>())
  const activityState = feedback[recordKey]
  useEffect(() => setOpen(false), [recordKey])

  function recordLaunch() {
    if (!isCanonicalCrmId(organizationId) || pending.current.has(recordKey)) return
    const launchKey = recordKey
    pending.current.add(launchKey)
    setFeedback(previous => ({ ...previous, [launchKey]: 'pending' }))
    void recordMeaningfulSalesInteraction({
      interactionId: crypto.randomUUID(),
      organizationId: organizationId!,
      contactId: isCanonicalCrmId(contactId) ? contactId! : null,
      opportunityId: isCanonicalCrmId(opportunityId) ? opportunityId! : null,
      interactionType: 'DEMO',
      occurredAt: new Date().toISOString(),
    }).then(() => setFeedback(previous => ({ ...previous, [launchKey]: 'recorded' })))
      .catch(() => setFeedback(previous => ({ ...previous, [launchKey]: 'unavailable' })))
      .finally(() => pending.current.delete(launchKey))
  }

  function link(route: SalesDemoRoute, className = 'sales-demo-route') {
    return <a
      className={className}
      href={buildSalesDemoUrl(config.publicSimulationOrigin, route)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => { recordLaunch(); setOpen(false) }}
    >
      <span><strong>{route.label}</strong><small>{route.summary}</small></span>
      <ExternalLink size={15} aria-hidden="true" />
    </a>
  }

  return <div className={`sales-demo-launcher${compact ? ' compact' : ''}`} data-demo-record={recordKey} onKeyDown={event => { if (event.key === 'Escape') setOpen(false) }}>
    <div className="sales-demo-actions">
      {audience ? <a
        className="primary-btn sales-demo-primary"
        href={buildSalesDemoUrl(config.publicSimulationOrigin, RECOMMENDED_SALES_DEMOS[audience])}
        target="_blank"
        rel="noopener noreferrer"
        onClick={recordLaunch}
        aria-label={`${label} — recommended ${audience === 'agency' ? 'Agency' : 'Business Owner'} experience`}
      >
        <Presentation size={16} aria-hidden="true" /> {label}
      </a> : <button className="primary-btn sales-demo-primary" type="button" onClick={() => setOpen(value => !value)} aria-expanded={open}>
        <Presentation size={16} aria-hidden="true" /> {label}
      </button>}
      {audience && <button className="sales-demo-menu-trigger" type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-label="Choose a demo scenario">
        Scenarios <ChevronDown size={14} aria-hidden="true" />
      </button>}
    </div>
    {open && <div className="sales-demo-menu" role="group" aria-label={audience ? `${audience} demo scenarios` : 'Choose a demo audience'}>
      {audience ? <>
        <span className="sales-demo-menu-label">Choose the customer pain to demonstrate</span>
        {SALES_DEMO_ROUTES[audience].map(route => <div key={route.key}>{link(route)}</div>)}
      </> : <>
        <span className="sales-demo-menu-label">Prospect type is not verified. Choose for this conversation.</span>
        {(['owner', 'agency'] as const).map(choice => <div key={choice}>{link(RECOMMENDED_SALES_DEMOS[choice])}</div>)}
      </>}
    </div>}
    {activityState === 'pending' && <span className="sales-demo-activity" role="status">Recording demo launch for this record…</span>}
    {activityState === 'recorded' && <span className="sales-demo-activity" role="status"><ShieldCheck size={12} /> Demo launch recorded for this record</span>}
    {activityState === 'unavailable' && <span className="sales-demo-activity warning" role="status">Demo opened; CRM activity was not recorded.</span>}
  </div>
}
